import type {
  AgendaCalendarAppointment,
  AgendaCalendarResponse,
  AgendaCalendarSlot,
  AgendaScheduleBlock,
  Id,
  Professional,
} from "../types";

type CalendarItem =
  | {
      kind: "appointment";
      starts_at: string;
      appointment: AgendaCalendarAppointment;
    }
  | {
      kind: "slot";
      starts_at: string;
      slot: AgendaCalendarSlot;
    }
  | {
      kind: "block";
      starts_at: string;
      block: AgendaScheduleBlock;
    };

function dayKey(value: string | Date) {
  const date = value instanceof Date ? value : new Date(value);

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDay(date: Date) {
  return new Intl.DateTimeFormat("es-AR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(date);
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

function appointmentTypeLabel(type?: string) {
  const labels: Record<string, string> = {
    evaluation: "Evaluación",
    treatment: "Tratamiento",
    follow_up: "Seguimiento",
    control: "Control",
    consultation: "Consulta",
  };

  return labels[type || ""] || type || "Turno";
}

function buildDays(from: string, to: string) {
  const days: Date[] = [];
  const current = new Date(from);
  const end = new Date(to);

  current.setHours(0, 0, 0, 0);

  while (current < end && days.length < 31) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
}

function itemsForProfessional(
  calendar: AgendaCalendarResponse,
  appointments: AgendaCalendarAppointment[],
  professional: Professional,
  day: Date
) {
  const key = dayKey(day);
  const items: CalendarItem[] = [];

  for (const appointment of appointments) {
    if (
      String(appointment.professional_id) === String(professional.id) &&
      dayKey(appointment.starts_at) === key
    ) {
      items.push({
        kind: "appointment",
        starts_at: appointment.starts_at,
        appointment,
      });
    }
  }

  for (const block of calendar.schedule_blocks) {
    if (
      block.entity_type === "PROFESSIONAL" &&
      String(block.entity_id) === String(professional.id) &&
      dayKey(block.starts_at) === key
    ) {
      items.push({
        kind: "block",
        starts_at: block.starts_at,
        block,
      });
    }
  }

  for (const slot of calendar.appointment_slots) {
    if (
      slot.status === "available" &&
      String(slot.professional_id) === String(professional.id) &&
      dayKey(slot.starts_at) === key
    ) {
      items.push({
        kind: "slot",
        starts_at: slot.starts_at,
        slot,
      });
    }
  }

  return items.sort(
    (a, b) =>
      new Date(a.starts_at).getTime() -
      new Date(b.starts_at).getTime()
  );
}

export default function AgendaCalendarGrid({
  calendar,
  appointments,
  onAppointmentClick,
  onSlotClick,
}: {
  calendar: AgendaCalendarResponse;
  appointments: AgendaCalendarAppointment[];
  onAppointmentClick: (appointment: AgendaCalendarAppointment) => void;
  onSlotClick: (slot: AgendaCalendarSlot) => void;
}) {
  const days = buildDays(calendar.range.from, calendar.range.to);
  const professionals = calendar.professionals;

  if (professionals.length === 0) {
    return (
      <div className="agenda-calendar-empty">
        No hay profesionales disponibles para esta vista.
      </div>
    );
  }

  return (
    <div className="agenda-calendar">
      {days.map((day) => (
        <section className="agenda-calendar-day" key={dayKey(day)}>
          <header className="agenda-calendar-day-header">
            <h2>{formatDay(day)}</h2>

            <span>
              {
                appointments.filter(
                  (appointment) =>
                    dayKey(appointment.starts_at) === dayKey(day)
                ).length
              }{" "}
              turnos
            </span>
          </header>

          <div
            className="agenda-calendar-columns"
            style={{
              gridTemplateColumns: `repeat(${professionals.length}, minmax(220px, 1fr))`,
            }}
          >
            {professionals.map((professional) => {
              const items = itemsForProfessional(
                calendar,
                appointments,
                professional,
                day
              );

              return (
                <div
                  className="agenda-calendar-professional"
                  key={professional.id}
                >
                  <div className="agenda-calendar-professional-header">
                    <strong>{professional.full_name}</strong>
                    {professional.specialty && (
                      <span>{professional.specialty}</span>
                    )}
                  </div>

                  <div className="agenda-calendar-events">
                    {items.map((item) => {
                      if (item.kind === "appointment") {
                        return (
                          <button
                            className={`agenda-calendar-item agenda-calendar-appointment agenda-calendar-appointment-${item.appointment.status || "scheduled"}`}
                            key={`appointment-${item.appointment.id}`}
                            onClick={() =>
                              onAppointmentClick(item.appointment)
                            }
                          >
                            <span className="agenda-calendar-time">
                              {formatTime(item.appointment.starts_at)}
                            </span>
                            <strong>
                              {item.appointment.contact_name ||
                                "Paciente"}
                            </strong>
                            <span>
                              {appointmentTypeLabel(
                                item.appointment.appointment_type
                              )}
                            </span>
                          </button>
                        );
                      }

                      if (item.kind === "block") {
                        return (
                          <div
                            className="agenda-calendar-item agenda-calendar-block"
                            key={`block-${item.block.id}`}
                          >
                            <span className="agenda-calendar-time">
                              {formatTime(item.block.starts_at)}–
                              {formatTime(item.block.ends_at)}
                            </span>
                            <strong>Bloqueado</strong>
                            <span>
                              {item.block.notes ||
                                item.block.reason}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <button
                          className="agenda-calendar-item agenda-calendar-slot"
                          key={`slot-${item.slot.id}`}
                          onClick={() => onSlotClick(item.slot)}
                        >
                          <span className="agenda-calendar-time">
                            {formatTime(item.slot.starts_at)}
                          </span>
                          <strong>Disponible</strong>
                          <span>{item.slot.slot_type}</span>
                        </button>
                      );
                    })}

                    {items.length === 0 && (
                      <div className="agenda-calendar-no-events">
                        Sin actividad
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
